// FirmaConnList.tsx — Inline-Liste „Verbindungen zu Firmen" (Beteiligung/Tochter/Mutter).
//
// Paket A5 (Plan 2026-10-01, User 02.10.2026): „+ Verbindung zu Unternehmen"
// fragt zuerst in einem eigenen Popup, um welche Art Verbindung es geht:
//   - Unternehmen im Verbund             → dieses Popup, Rollen Tochter/Mutter/Schwester
//   - Unternehmen außerhalb des Verbunds → dieses Popup, Rollen Beteiligung/Joint Venture
//   - Verantwortung fürs Unternehmen (extern, z. B. Steuerberater)
//                                        → das Popup der Karte „Verantwortung fürs
//                                          Unternehmen", Firma als Gegenüber
// Beim Bearbeiten bestehender Einträge stehen alle Rollen zur Wahl.

import * as React from "react";
import { FieldRow, LabeledSelect, LabeledInput } from "../forms/formPrimitives";
import { EintragsListe, type EintragsListeSteuerung } from "../forms/EintragsListe";
import { firmaConnText, refName, zeilenAnzeigeVon } from "./beziehungsAnzeige";
import { ContactRefField } from "./ContactRefField";
import { useRelStyles } from "./styles";
import { eintragAnteile } from "../../data/anteilsbeziehung";
import { FIRMA_CONN_ROLES, FIRMA_CONN_ROLE_VALUES, uid } from "./types";
import type { FirmaConnItem, ContactRef } from "./types";
import type { BeziehungsZeile, TabellenAblage } from "../forms/BeziehungsTabelle";
import { ZIEH_ART_FIRMENAKTE, ziehDatenVon, ziehMarkerVon, type ZiehQuelle } from "./beziehungsZiehen";
import { FIRMA_CONN_GRUPPEN, type FirmaConnGruppe } from "../../data/firmenverbindungen";
import { auswahl } from "../redesign/bestaetigen";

/** Die drei Wege hinter „+ Verbindung zu Unternehmen" (Paket A5). */
export const VERBINDUNGS_ART = {
  verbund: "Unternehmen im Verbund (Mutter-, Tochter-, Schwesterfirma)",
  aussen: "Unternehmen außerhalb des Verbunds (Beteiligung, Joint Venture)",
  verantwortung: "Verantwortung fürs Unternehmen (extern, z. B. Steuerberater)",
} as const;
export type VerbindungsArt = keyof typeof VERBINDUNGS_ART;

/** Fragt die Art im eigenen Popup ab; `null` = abgebrochen. */
export async function verbindungsArtFragen(
  mitVerantwortung: boolean,
  frage: typeof auswahl = auswahl,
): Promise<VerbindungsArt | null> {
  const arten = (Object.keys(VERBINDUNGS_ART) as VerbindungsArt[]).filter(
    (a) => mitVerantwortung || a !== "verantwortung",
  );
  const wahl = await frage({
    titel: "Verbindung zu Unternehmen",
    text: "Um welche Art Verbindung geht es?",
    optionen: arten.map((a) => VERBINDUNGS_ART[a]),
    ja: "Weiter",
  });
  return arten.find((a) => VERBINDUNGS_ART[a] === wahl) ?? null;
}

export type FirmaConnSteuerung = {
  /** Popup für einen neuen Eintrag öffnen — mit den Rollen der Gruppe und Vorbelegung. */
  neu: (gruppe: FirmaConnGruppe | null, vorlage?: Partial<FirmaConnItem>) => void;
};

/**
 * Doppelte Ids zusammenlegen — der zuletzt übernommene Eintrag gewinnt. So
 * ersetzt ein Popup, das per Ziehen mit einem BESTEHENDEN Eintrag geöffnet
 * wurde (Paket A6), diesen beim Übernehmen, statt ihn zu verdoppeln.
 */
export function letzterGewinnt<T extends { id: string }>(liste: T[]): T[] {
  const letzter = new Map(liste.map((x, i) => [x.id, i]));
  return liste.filter((x, i) => letzter.get(x.id) === i);
}

export function FirmaConnList({
  value,
  onChange,
  gesellschafterFirmen = [],
  ausblenden,
  onVerantwortungExtern,
  steuerung,
  ziehen,
}: {
  value: FirmaConnItem[];
  onChange: (next: FirmaConnItem[]) => void;
  /** Nur-Lese-Zeilen aus einer anderen Karte (derzeit ungenutzt). */
  gesellschafterFirmen?: BeziehungsZeile[];
  /**
   * Einträge, die in DIESER Liste nicht stehen, aber gespeichert bleiben —
   * Mutter und Beteiligung an dieser Firma stehen bei „Unternehmensleitung
   * und Gesellschafter" (User 28.09.2026). Beim Ändern der Liste werden sie
   * unverändert wieder angehängt.
   */
  ausblenden?: (it: FirmaConnItem) => boolean;
  /**
   * Dritter Weg der Abfrage (A5): öffnet das Popup „Verantwortung fürs
   * Unternehmen" mit einer Firma als Gegenüber. Ohne: nur die zwei Wege.
   */
  onVerantwortungExtern?: () => void;
  /** Popup von außen öffnen (z. B. Ziehen & Ablegen). */
  steuerung?: React.MutableRefObject<FirmaConnSteuerung | null>;
  /** Ziehen & Ablegen zwischen den Listen der Firmenakte (Paket A6). */
  ziehen?: { ablage: TabellenAblage; quelle: (it: FirmaConnItem) => ZiehQuelle };
}) {
  const s = useRelStyles();
  const verborgen = ausblenden ? value.filter(ausblenden) : [];
  const sichtbar = ausblenden ? value.filter((it) => !ausblenden(it)) : value;
  const innen = React.useRef<EintragsListeSteuerung<FirmaConnItem> | null>(null);
  // Rollen-Gruppe des gerade neu angelegten Eintrags (A5). Beim Bearbeiten
  // (Stift) gilt sie nicht — dort stehen alle Rollen zur Wahl.
  const [gruppe, setGruppe] = React.useState<FirmaConnGruppe | null>(null);

  const neuOeffnen = React.useCallback((g: FirmaConnGruppe | null, vorlage?: Partial<FirmaConnItem>) => {
    setGruppe(g);
    innen.current?.neu({ ...(g ? { role: FIRMA_CONN_GRUPPEN[g][0] } : {}), ...(vorlage ?? {}) });
  }, []);

  React.useEffect(() => {
    if (!steuerung) return;
    steuerung.current = { neu: neuOeffnen };
    return () => {
      steuerung.current = null;
    };
  }, [steuerung, neuOeffnen]);

  const hinzufuegen = async () => {
    const art = await verbindungsArtFragen(!!onVerantwortungExtern);
    if (!art) return;
    if (art === "verantwortung") {
      onVerantwortungExtern?.();
      return;
    }
    neuOeffnen(art);
  };

  return (
    <EintragsListe<FirmaConnItem>
      items={sichtbar}
      onChange={(n) => onChange(letzterGewinnt([...verborgen, ...n]))}
      neu={() => ({ id: uid(), ref: null, role: FIRMA_CONN_ROLE_VALUES[0], share: "" })}
      hinzufuegenLabel="Verbindung zu Unternehmen"
      onHinzufuegen={() => void hinzufuegen()}
      steuerung={innen}
      dialogTitel="Verbindung zu einem Unternehmen"
      zeile={(it) => ({
        titel: refName(it.ref) || "Ohne Namen",
        zusatz: firmaConnText({ role: it.role }),
        anteile: eintragAnteile(it) || undefined,
        ...zeilenAnzeigeVon(it.ref),
        ...(ziehen && it.ref
          ? { ziehDaten: ziehDatenVon(ziehen.quelle(it)), ziehMarker: ziehMarkerVon(ziehen.quelle(it)) }
          : {}),
      })}
      ziehArt={ziehen ? ZIEH_ART_FIRMENAKTE : undefined}
      ziehbar={!!ziehen}
      ablage={ziehen?.ablage}
      leerText="Noch keine verbundenen Firmen (Beteiligung, Tochter-/Mutterfirma)."
      spalten={{ name: "Name", rolle: "Rolle", anteile: "Anteile" }}
      fremdZeilen={gesellschafterFirmen}
      istLeer={(it) => !it.ref}
      // Die Art wählt der Anwender selbst — etwa nach dem Ziehen in die
      // Gesellschaftertabelle ist sie bewusst leer (User 02.10.2026).
      pruefen={(it) => (it.role ? null : "Bitte die Art der Verbindung wählen.")}
      renderFelder={(it, update, ctx) => {
        const rollen =
          ctx.entwurf && gruppe
            ? FIRMA_CONN_ROLES.filter((r) => (FIRMA_CONN_GRUPPEN[gruppe] as readonly string[]).includes(r.value))
            : FIRMA_CONN_ROLES;
        // Eine Bestandsrolle, die nicht in der Auswahl steht, bleibt wählbar —
        // sonst zeigte das Feld etwas anderes, als gespeichert ist.
        const optionen = !it.role || rollen.some((r) => r.value === it.role)
          ? rollen
          : [...rollen, { value: it.role, label: it.role }];
        return (
          <>
            <div className={s.refField}>
              <span className={s.sectionLabel}>Firma</span>
              <ContactRefField
                wantType="Firma"
                idBase={`firmaConn-${it.id}`}
                value={it.ref}
                onChange={(ref: ContactRef | null) => update({ ref })}
              />
            </div>
            <FieldRow columns={2}>
              <LabeledSelect
                label="Art der Verbindung"
                name={`firmaConn-role-${it.id}`}
                value={it.role}
                onChange={(v) => update({ role: v })}
                options={optionen.map((r) => ({ value: r.value, label: r.label }))}
                placeholder="Art"
              />
              <LabeledInput
                label="Anteile"
                name={`firmaConn-share-${it.id}`}
                value={it.share ?? ""}
                onChange={(v) => update({ share: v })}
                placeholder="z. B. 50 %"
              />
            </FieldRow>
          </>
        );
      }}
    />
  );
}
